export interface RelationshipSignatureDefinition {
  id: string
  name: string
  icon?: string
  tooltip?: string
  color?: string
}

export interface RelationshipSignatureRow {
  [key: string]: any
  signatures?: any
}

export interface RelationshipSignatureGroup {
  key: string
  name: string
  description: string
  signatures: string[]
  color: string
  priority: number
}

export const RELATIONSHIP_SIGNATURE_GROUPS: RelationshipSignatureGroup[] = [
  {
    key: 'bridge_builder',
    name: 'Bridge Builder',
    description: 'Connects groups and helps information move across network gaps.',
    signatures: ['Brokerage', 'Strategist', 'Linker'],
    color: '#0F9F9A',
    priority: 2,
  },
  {
    key: 'network_hub',
    name: 'Network Hub',
    description: 'Has many direct connections or is connected to other influential people.',
    signatures: ['Connectivity', 'Hub', 'Influence'],
    color: '#6C30CC',
    priority: 3,
  },
  {
    key: 'community_anchor',
    name: 'Community Anchor',
    description: 'Anchors a local community where nearby connections are tightly connected.',
    signatures: ['Cohesion', 'Community Leader'],
    color: '#C77912',
    priority: 4,
  },
  {
    key: 'broad_reacher',
    name: 'Broad Reacher',
    description: 'Can reach the broader organization efficiently through the relationship network.',
    signatures: ['Reachability', 'Ripple', 'Collective'],
    color: '#2563EB',
    priority: 5,
  },
  {
    key: 'isolation_risk',
    name: 'Isolation Risk',
    description: 'May be disconnected, siloed, or difficult for others to reach.',
    signatures: ['Insular', 'Peripheral', 'Distant'],
    color: '#64748B',
    priority: 1,
  },
]

function numberOrNull(value: any): number | null {
  if (value === undefined || value === null || value === '') return null
  const number = Number(value)
  return Number.isFinite(number) ? number : null
}

export function normalizeRelationshipSignatureKey(value: any): string {
  return String(value || '')
    .replace(/([a-z])([A-Z])/g, '$1 $2')
    .replace(/[_-]+/g, ' ')
    .trim()
    .toLowerCase()
}

function compactSignatureKey(value: any): string {
  return normalizeRelationshipSignatureKey(value).replace(/\s+/g, '')
}

function canonicalSignatureId(value: any): string {
  const normalized = normalizeRelationshipSignatureKey(value)

  if (normalized === 'influence i' || normalized === 'influencei') return 'collective'
  if (normalized === 'influence ii' || normalized === 'influenceii') return 'influence'
  if (normalized === 'community leader') return 'community_leader'
  if (normalized === 'insular') return 'insularity'

  return normalized.replace(/\s+/g, '_')
}

function addSignatureLookupValue(lookup: Record<string, boolean>, value: any) {
  const normalized = normalizeRelationshipSignatureKey(value)
  if (!normalized) return

  lookup[normalized] = true
  lookup[compactSignatureKey(normalized)] = true
  lookup[canonicalSignatureId(normalized)] = true
}

function backendSignatureLookup(signatures: any): Record<string, boolean> {
  const lookup: Record<string, boolean> = {}

  if (Array.isArray(signatures)) {
    signatures.forEach(signature => {
      if (typeof signature === 'string') {
        addSignatureLookupValue(lookup, signature)
      } else if (signature && typeof signature === 'object') {
        addSignatureLookupValue(lookup, signature.id)
        addSignatureLookupValue(lookup, signature.name)
        addSignatureLookupValue(lookup, signature.type)
      }
    })

    return lookup
  }

  if (signatures && typeof signatures === 'object') {
    Object.keys(signatures).forEach(key => {
      if (signatures[key]) {
        addSignatureLookupValue(lookup, key)
      }
    })
  }

  return lookup
}

function hasBackendSignature(lookup: Record<string, boolean>, signature: RelationshipSignatureDefinition): boolean {
  const values = [signature.id, signature.name, canonicalSignatureId(signature.id), compactSignatureKey(signature.name)]
  return values.some(value => Boolean(value && lookup[String(value)]))
}

function signatureValueLookup(signatures: any): Record<string, boolean> {
  return backendSignatureLookup(signatures)
}

function groupMatchesLookup(group: RelationshipSignatureGroup, lookup: Record<string, boolean>): boolean {
  return group.signatures.some(signature => {
    const normalized = normalizeRelationshipSignatureKey(signature)
    return Boolean(
      lookup[normalized] ||
      lookup[normalized.replace(/\s+/g, '')] ||
      lookup[canonicalSignatureId(normalized)],
    )
  })
}

function rowValue(row: RelationshipSignatureRow, key: string): number | null {
  return numberOrNull(row[key])
}

function metricMeetsSignatureRule(row: RelationshipSignatureRow, signatureId: string): boolean {
  const brokerage = rowValue(row, 'brokerage')
  const cohesion = rowValue(row, 'cohesion')
  const collective = rowValue(row, 'collective')
  const connectivity = rowValue(row, 'connectivity')
  const influence = rowValue(row, 'influence')
  const reachability = rowValue(row, 'reachability')
  const ripple = rowValue(row, 'ripple')
  const hub = rowValue(row, 'hub')
  const strategist = rowValue(row, 'strategist')
  const communityLeader = rowValue(row, 'community_leader')
  const linker = rowValue(row, 'linker')
  const insularity = rowValue(row, 'insularity')

  switch (canonicalSignatureId(signatureId)) {
    case 'brokerage':
      return brokerage !== null && brokerage >= 0.85
    case 'cohesion':
      return cohesion !== null && cohesion >= 0.6
    case 'connectivity':
      return connectivity !== null && connectivity >= 0.85
    case 'collective':
      return collective !== null && collective >= 0.85
    case 'influence':
      return influence !== null && influence >= 0.85
    case 'reachability':
      return reachability !== null && reachability >= 0.85
    case 'ripple':
      return ripple !== null && ripple >= 0.85
    case 'hub':
      return hub !== null
        ? hub >= 0.85
        : connectivity !== null && reachability !== null && connectivity * reachability >= 0.85
    case 'strategist':
      return strategist !== null
        ? strategist >= 0.85
        : brokerage !== null && influence !== null && brokerage * influence >= 0.85
    case 'community_leader':
      return (communityLeader !== null && communityLeader >= 0.85) ||
        (cohesion !== null && connectivity !== null && cohesion >= 0.5 && connectivity >= 0.7)
    case 'linker':
      return (linker !== null && linker >= 0.85) ||
        (brokerage !== null && ripple !== null && brokerage >= 0.4 && ripple >= 0.8)
    case 'insularity':
      return insularity !== null && insularity >= 0.85
    case 'peripheral':
      return connectivity !== null && connectivity <= 0.1
    case 'distant':
      return reachability !== null && reachability <= 0.1
    default:
      return false
  }
}

export function deriveRelationshipSignatures(
  row: RelationshipSignatureRow,
  definitions: RelationshipSignatureDefinition[],
): RelationshipSignatureDefinition[] {
  if (!row || !Array.isArray(definitions)) return []

  const backendLookup = backendSignatureLookup(row.signatures)
  const added: Record<string, boolean> = {}
  const derived: RelationshipSignatureDefinition[] = []

  definitions.forEach(signature => {
    const canonicalId = canonicalSignatureId(signature.id || signature.name)
    if (!canonicalId || added[canonicalId]) return

    if (!hasBackendSignature(backendLookup, signature) && !metricMeetsSignatureRule(row, canonicalId)) {
      return
    }

    added[canonicalId] = true
    derived.push({
      ...signature,
      id: canonicalId,
      color: signature.color || '',
    })
  })

  return derived
}

export function getRelationshipSignatureGroups(signatures: any): RelationshipSignatureGroup[] {
  const lookup = signatureValueLookup(signatures)

  return RELATIONSHIP_SIGNATURE_GROUPS
    .filter(group => groupMatchesLookup(group, lookup))
    .sort((a, b) => a.priority - b.priority)
}

export function expandRelationshipSignatureGroupKeys(keys: string[]): string[] {
  if (!Array.isArray(keys)) return []

  const selected = new Set(keys)
  const seen: Record<string, boolean> = {}
  const signatures: string[] = []

  RELATIONSHIP_SIGNATURE_GROUPS.forEach(group => {
    if (!selected.has(group.key)) return

    group.signatures.forEach(signature => {
      const normalized = normalizeRelationshipSignatureKey(signature)
      if (seen[normalized]) return
      seen[normalized] = true
      signatures.push(signature)
    })
  })

  return signatures
}
